import { namedKeyLabel, orchestrationEventUrl } from '@app/pages/admin/orchestration-identity.util';
import { canonicalProcessKey } from '@app/pages/admin/orchestration-process-keys.util';

export type ProcessEventKind = 'core' | 'domain';

export interface ProcessEventChipView {
  eventKey: string;
  eventLabel: string;
  eventUrl: string | null;
  kind: ProcessEventKind;
}

/** Core dispatcher events shown once on the processes catalog heading (ORCHESTRATION.md §3). */
export const CORE_PROCESS_EVENT_KEYS = [
  'assignment.completed',
  'assignment.failed',
] as const;

/**
 * Domain events raised by a specific process. Keep in sync with ORCHESTRATION.md
 * «Кто публикует».
 */
export const DOMAIN_EVENT_KEYS_BY_PROCESS: Readonly<Record<string, readonly string[]>> = {
  'satellite.download-product': ['satellite.product-downloaded'],
  'orchestration.probe': ['orchestration.probe.tick'],
  'analitic.request-farmer-report-for-finished-bids': ['analitic.farmer-report.candidate-rejected'],
  'crop.export-bid-templates': ['crop.bid-templates.new-bids'],
};

/** Domain events whose occurrence payload includes processDefinitionKey, so a rule may store that source. */
const DOMAIN_EVENTS_WITH_PROCESS_KEY = new Set([
  'orchestration.probe.tick',
  'crop.bid-templates.new-bids',
]);

export interface DomainEventSourceProcess {
  processKey: string;
  /** False when a stored source would never match (payload has no processDefinitionKey). */
  persist: boolean;
}

/** The single process that publishes this event, or null when many processes can raise it. */
export function domainEventSourceProcess(eventKey: string): DomainEventSourceProcess | null {
  const lookup = eventKey.trim().toLowerCase();
  if (!lookup) {
    return null;
  }
  const owners = Object.entries(DOMAIN_EVENT_KEYS_BY_PROCESS)
    .filter(([, keys]) => keys.some(key => key.toLowerCase() === lookup))
    .map(([processKey]) => processKey);
  if (owners.length !== 1) {
    return null;
  }
  return {
    processKey: owners[0],
    persist: DOMAIN_EVENTS_WITH_PROCESS_KEY.has(lookup),
  };
}

export function publishedEventsForProcess(
  processKey: string,
  events: Array<{ key: string; displayName?: string }> | null | undefined,
  extraEventKeys: readonly string[] = [],
): ProcessEventChipView[] {
  const key = canonicalProcessKey(processKey);
  if (!key) {
    return [];
  }
  const catalog = new Map<string, { key: string; displayName?: string }>();
  for (const item of events ?? []) {
    const eventKey = item.key?.trim();
    if (eventKey) {
      catalog.set(eventKey.toLowerCase(), item);
    }
  }
  const catalogLoaded = catalog.size > 0;
  const seen = new Set<string>();
  const chips: ProcessEventChipView[] = [];

  const push = (eventKey: string, kind: ProcessEventKind): void => {
    const normalized = eventKey.trim();
    if (!normalized) {
      return;
    }
    const lookup = normalized.toLowerCase();
    if (seen.has(lookup)) {
      return;
    }
    const definition = catalog.get(lookup);
    if (catalogLoaded && !definition && kind !== 'core') {
      return;
    }
    seen.add(lookup);
    chips.push({
      eventKey: definition?.key ?? normalized,
      eventLabel: namedKeyLabel(events, definition?.key ?? normalized) || normalized,
      eventUrl: orchestrationEventUrl(definition?.key ?? normalized),
      kind,
    });
  };

  for (const eventKey of DOMAIN_EVENT_KEYS_BY_PROCESS[key] ?? []) {
    push(eventKey, 'domain');
  }
  for (const eventKey of extraEventKeys) {
    const lookup = eventKey.trim().toLowerCase();
    if ((CORE_PROCESS_EVENT_KEYS as readonly string[]).includes(lookup)) {
      continue;
    }
    push(eventKey, 'domain');
  }
  return chips;
}

export function coreProcessEvents(
  events: Array<{ key: string; displayName?: string }> | null | undefined,
): ProcessEventChipView[] {
  const catalog = new Map<string, { key: string; displayName?: string }>();
  for (const item of events ?? []) {
    const eventKey = item.key?.trim();
    if (eventKey) {
      catalog.set(eventKey.toLowerCase(), item);
    }
  }
  return CORE_PROCESS_EVENT_KEYS.map(eventKey => {
    const definition = catalog.get(eventKey);
    const key = definition?.key ?? eventKey;
    return {
      eventKey: key,
      eventLabel: namedKeyLabel(events, key) || key,
      eventUrl: orchestrationEventUrl(key),
      kind: 'core' as const,
    };
  });
}

export function sourceEventKeysByProcess(
  triggers: Array<{ eventKey?: string | null; sourceProcessDefinitionKey?: string | null }> | null | undefined,
): Map<string, string[]> {
  const byProcess = new Map<string, string[]>();
  for (const trigger of triggers ?? []) {
    const eventKey = trigger.eventKey?.trim() ?? '';
    const sourceKey = canonicalProcessKey(trigger.sourceProcessDefinitionKey ?? '');
    if (!eventKey || !sourceKey) {
      continue;
    }
    const items = byProcess.get(sourceKey) ?? [];
    if (!items.some(item => item.toLowerCase() === eventKey.toLowerCase())) {
      items.push(eventKey);
    }
    byProcess.set(sourceKey, items);
  }
  return byProcess;
}
